<template>
    <div class="artist-platform-progress" :class="{ 'is-wrap': wrapLayout }">
        <!-- 默认：横向紧凑排列（不换行） -->
        <template v-if="!wrapLayout">
            <div class="progress-row">
                <span class="progress-title">
                    平台艺人关联
                    <el-tooltip content="展示各平台艺人主页关联进度" placement="top">
                        <i class="el-icon-info info-icon"></i>
                    </el-tooltip>
                </span>
                <span class="artist-count">涉及 {{ totalArtistCount }} 位艺人</span>
                
                <div class="platform-list">
                    <el-tooltip
                        v-for="platform in platformList"
                        :key="platform.type"
                        placement="top"
                        effect="dark"
                        :open-delay="200"
                    >
                        <div class="platform-item">
                            <svg-icon :icon-class="platform.icon"></svg-icon>
                            <span class="platform-name">{{ platform.name }}</span>
                            <span class="platform-count" :class="{'is-complete': isPlatformComplete(platform.type)}">{{ getProgressNumerator(platform.type) }}/{{ getProgressDenominator(platform.type) }}</span>
                        </div>
                        <div slot="content" class="platform-tooltip">
                            <div class="tooltip-title">{{ platform.name }} <span class="tooltip-total" :class="{'is-complete': isPlatformComplete(platform.type)}">{{ getProgressNumerator(platform.type) }}/{{ getProgressDenominator(platform.type) }}</span></div>
                            <div class="tooltip-row">
                                <span class="dot dot-green"></span>
                                <span class="tooltip-label">已关联</span>
                                <span class="tooltip-count">{{ getPlatformLinkedCount(platform.type) }}</span>
                            </div>
                            <div class="tooltip-row">
                                <span class="dot dot-orange"></span>
                                <span class="tooltip-label">{{ getSpecialStatusLabel(platform.type) }}</span>
                                <span class="tooltip-count">{{ getPlatformCuratedCount(platform.type) }}</span>
                            </div>
                            <div class="tooltip-row">
                                <span class="dot dot-gray"></span>
                                <span class="tooltip-label">待处理</span>
                                <span class="tooltip-count">{{ getPlatformPendingCount(platform.type) }}</span>
                            </div>
                        </div>
                    </el-tooltip>
                </div>

                <span class="handle-btn" @click="handleClick" v-if="isEditable">
                    {{ actionText }}
                    <i class="el-icon-arrow-right"></i>
                </span>
            </div>
        </template>

        <!-- wrapLayout=true：2列网格卡片布局（更紧凑小尺寸） -->
        <template v-else>
            <div class="progress-header">
                <div class="header-left">
                    <span class="title-bar"></span>
                    <span class="progress-title">平台艺人关联</span>
                    <span class="artist-count">涉及 {{ totalArtistCount }} 位艺人</span>
                    <el-tooltip content="展示各平台艺人主页关联进度" placement="top">
                        <i class="el-icon-info info-icon"></i>
                    </el-tooltip>
                </div>
                <span class="handle-btn" @click="handleClick" v-if="isEditable">
                    {{ actionText }}
                    <i class="el-icon-arrow-right"></i>
                </span>
            </div>

            <div class="platform-grid">
                <el-tooltip
                    v-for="platform in platformList"
                    :key="platform.type"
                    placement="top"
                    effect="dark"
                    :open-delay="200"
                >
                    <div class="platform-card">
                        <div class="card-icon">
                            <svg-icon :icon-class="platform.icon"></svg-icon>
                        </div>
                        <span class="platform-name">{{ platform.name }}</span>
                        <span class="platform-count" :class="{'is-complete': isPlatformComplete(platform.type)}">
                            <span class="count-num">{{ getProgressNumerator(platform.type) }}</span>
                            <span class="count-sep">/</span>
                            <span class="count-total">{{ getProgressDenominator(platform.type) }}</span>
                        </span>
                    </div>
                    <div slot="content" class="platform-tooltip">
                        <div class="tooltip-title">{{ platform.name }} <span class="tooltip-total" :class="{'is-complete': isPlatformComplete(platform.type)}">{{ getProgressNumerator(platform.type) }}/{{ getProgressDenominator(platform.type) }}</span></div>
                        <div class="tooltip-row">
                            <span class="dot dot-green"></span>
                            <span class="tooltip-label">已关联</span>
                            <span class="tooltip-count">{{ getPlatformLinkedCount(platform.type) }}</span>
                        </div>
                        <div class="tooltip-row">
                            <span class="dot dot-orange"></span>
                            <span class="tooltip-label">{{ getSpecialStatusLabel(platform.type) }}</span>
                            <span class="tooltip-count">{{ getPlatformCuratedCount(platform.type) }}</span>
                        </div>
                        <div class="tooltip-row">
                            <span class="dot dot-gray"></span>
                            <span class="tooltip-label">待处理</span>
                            <span class="tooltip-count">{{ getPlatformPendingCount(platform.type) }}</span>
                        </div>
                    </div>
                </el-tooltip>
            </div>
        </template>
    </div>
</template>

<script>
export default {
    name: 'ArtistPlatformProgress',
    props: {
        statsData: {
            type: Array,
            default: () => []
        },
        artistProgressData: {
            type: Array,
            default: () => []
        },
        isEditable: {
            type: Boolean,
            default: false
        },
        wrapLayout: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        totalArtistCount() {
            if (this.statsData && this.statsData.length > 0) {
                return this.statsData[0].totalArtistCount || 0;
            }
            return this.artistProgressData?.length || 0;
        },
        platformList() {
            return [
                { type: 1, name: '网易云音乐', icon: 'new-wyy' },
                { type: 2, name: 'QQ音乐', icon: 'new-qq' },
                { type: 3, name: 'Apple Music', icon: 'new-appleMusic' },
                { type: 4, name: 'Spotify', icon: 'new-spotify' },
            ];
        },
        useStatsData() {
            return this.statsData && this.statsData.length > 0;
        },
        hasPendingProgress() {
            if (this.useStatsData) {
                return this.statsData.some(s => (s.pendingCount || 0) > 0);
            }
            return this.artistProgressData.some(artist => {
                if (!artist.artistRelThirdPartyPlatforms) return true;
                const platforms = artist.artistRelThirdPartyPlatforms;
                return platforms.some(p => {
                    const status = Number(p.status);
                    const type = Number(p.platformType);
                    if (type === 3) {
                        return status !== 1 && status !== 2;
                    }
                    return status !== 1;
                }) || platforms.length < 4;
            });
        },
        actionText() {
            return '去处理';
        },
    },
    methods: {
        getPlatformStats(platformType) {
            if (!this.useStatsData) return null;
            return this.statsData.find(s => Number(s.platformType) === Number(platformType));
        },
        getProgressNumerator(platformType) {
            if (this.useStatsData) {
                const stats = this.statsData.find(s => Number(s.platformType) === Number(platformType));
                return stats ? (stats.progressNumerator || 0) : 0;
            }
            return this.getPlatformLinkedCount(platformType) + this.getPlatformCuratedCount(platformType);
        },
        /**
         * 获取指定平台进度分母
         * @param {Number} platformType 平台类型
         * @returns {Number}
         */
        getProgressDenominator(platformType) {
            if (this.useStatsData) {
                const stats = this.statsData.find(s => Number(s.platformType) === Number(platformType));
                return stats ? (stats.progressDenominator || 0) : 0;
            }
            return this.totalArtistCount;
        },
        isPlatformComplete(platformType) {
            return this.getProgressDenominator(platformType) > 0 && this.getPlatformLinkedCount(platformType) === this.getProgressDenominator(platformType);
        },
        getPlatformLinkedCount(platformType) {
            if (this.useStatsData) {
                const stats = this.statsData.find(s => Number(s.platformType) === Number(platformType));
                return stats ? (stats.linkedCount || 0) : 0;
            }
            if (!this.artistProgressData || this.artistProgressData.length === 0) return 0;
            return this.artistProgressData.filter(artist => {
                if (!artist.artistRelThirdPartyPlatforms) return false;
                const platform = artist.artistRelThirdPartyPlatforms.find(
                    p => Number(p.platformType) === Number(platformType)
                );
                if (!platform) return false;
                const status = Number(platform.status);
                if (platformType === 3) {
                    return status === 1 || status === 2;
                }
                return status === 1;
            }).length;
        },
        getPlatformCuratedCount(platformType) {
            if (this.useStatsData) {
                const stats = this.statsData.find(s => Number(s.platformType) === Number(platformType));
                return stats ? (stats.curatedCount || 0) : 0;
            }
            if (!this.artistProgressData || this.artistProgressData.length === 0) return 0;
            return this.artistProgressData.filter(artist => {
                if (!artist.artistRelThirdPartyPlatforms) return false;
                const platform = artist.artistRelThirdPartyPlatforms.find(
                    p => Number(p.platformType) === Number(platformType)
                );
                if (!platform) return false;
                return Number(platform.status) === 2;
            }).length;
        },
        getPlatformPendingCount(platformType) {
            if (this.useStatsData) {
                const stats = this.statsData.find(s => Number(s.platformType) === Number(platformType));
                return stats ? (stats.pendingCount || 0) : 0;
            }
            if (!this.artistProgressData || this.artistProgressData.length === 0) return this.totalArtistCount;
            const boundCount = this.getPlatformLinkedCount(platformType);
            const curatedCount = this.getPlatformCuratedCount(platformType);
            return this.totalArtistCount - boundCount - curatedCount;
        },
        getSpecialStatusLabel(platformType) {
            if (platformType === 3) {
                return '暂无主页';
            }
            return '新建主页';
        },
        handleClick() {
            this.$emit('handle-click', !this.isEditable || !this.hasPendingProgress);
        }
    }
};
</script>

<style lang="scss" scoped>
/* 默认：横向紧凑布局（不换行） */
.artist-platform-progress:not(.is-wrap) {
    background: #f5f7fa;
    border-radius: 8px;
    padding: 8px 12px;
    margin: 8px 0;
    display: inline-block;
}

.progress-row {
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    gap: 6px;
    width: max-content;
}

.progress-title {
    font-size: 13px;
    font-weight: 600;
    color: #303133;
    display: flex;
    align-items: center;
    gap: 4px;
    white-space: nowrap;
    flex-shrink: 0;
}

.info-icon {
    color: #909399;
    font-size: 13px;
    cursor: pointer;
    &:hover {
        color: #409eff;
    }
}

.artist-count {
    font-size: 12px;
    color: #606266;
    white-space: nowrap;
    flex-shrink: 0;
}

.platform-list {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
}

.platform-item {
    display: flex;
    align-items: center;
    gap: 3px;
    background: #fff;
    border-radius: 4px;
    padding: 3px 6px;
    font-size: 12px;
    white-space: nowrap;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.1);
        transform: translateY(-1px);
    }

    ::v-deep .svg-icon {
        width: 16px;
        height: 16px;
    }

    .platform-name {
        color: #303133;
        font-weight: 500;
    }

    .platform-count {
        color: #e6a23c;
        font-weight: 600;
        font-size: 13px;

        &.is-complete {
            color: #67c23a;
        }
    }
}

.handle-btn {
    font-size: 13px;
    color: #409eff;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 2px;
    font-weight: 500;
    white-space: nowrap;
    flex-shrink: 0;

    &:hover {
        color: #66b1ff;
    }

    i {
        font-size: 12px;
    }
}

/* wrapLayout=true：2列网格卡片布局（更紧凑小尺寸） */
.artist-platform-progress.is-wrap {
    background: linear-gradient(135deg, #f8f5ff 0%, #f0f7ff 100%);
    border-radius: 8px;
    border: 1px solid #e8e5ff;
    padding: 10px;
    margin: 8px 0;
}

.is-wrap .progress-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
}

.is-wrap .header-left {
    display: flex;
    align-items: center;
    gap: 6px;
}

.is-wrap .title-bar {
    width: 3px;
    height: 14px;
    background: linear-gradient(180deg, #722ed1 0%, #5b8def 100%);
    border-radius: 2px;
}

.is-wrap .progress-title {
    font-size: 14px;
    font-weight: 600;
    color: #1d1d1f;
    line-height: 1;
}

.is-wrap .info-icon {
    font-size: 12px;
}

.is-wrap .artist-count {
    font-size: 12px;
    color: #86868b;
    line-height: 1;
}

.is-wrap .platform-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 6px;
}

.is-wrap .platform-card {
    display: flex;
    align-items: center;
    gap: 6px;
    background: #fff;
    border-radius: 8px;
    padding: 8px 10px;
    cursor: pointer;
    transition: all 0.2s;
    border: 1px solid #f0f0f0;

    &:hover {
        box-shadow: 0 2px 8px 0 rgba(114, 46, 209, 0.1);
        transform: translateY(-1px);
        border-color: #d9d2ff;
    }

    .card-icon {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        ::v-deep .svg-icon {
            width: 18px;
            height: 18px;
        }
    }

    .platform-name {
        color: #1d1d1f;
        font-weight: 500;
        font-size: 13px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        flex: 1;
        min-width: 0;
    }

      .platform-count {
          font-weight: 600;
          font-size: 13px;
          margin-left: auto;
          flex-shrink: 0;
          color: #e6a23c;

          .count-sep {
              margin: 0 1px;
          }

          .count-total {
              font-weight: 500;
          }

          &.is-complete {
              color: #67c23a;
          }
      }
  }

.is-wrap .handle-btn {
    margin-left: 0;
    font-size: 14px;
    color: #722ed1;
    gap: 2px;
    font-weight: 600;
    line-height: 1;

    &:hover {
        color: #9254de;
    }

    i {
        font-size: 12px;
    }
}
</style>

<style lang="scss">
.platform-tooltip {
    min-width: 140px;

    .tooltip-title {
        font-size: 13px;
        font-weight: 600;
        margin-bottom: 8px;
        display: flex;
        justify-content: space-between;
        align-items: center;

        .tooltip-total {
            font-size: 12px;
            color: #e6a23c;
            font-weight: 600;

            &.is-complete {
                color: #67c23a;
            }
        }
    }

    .tooltip-row {
        display: flex;
        align-items: center;
        font-size: 12px;
        line-height: 22px;

        .dot {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            margin-right: 8px;
            flex-shrink: 0;

            &.dot-green {
                background: #67c23a;
            }

            &.dot-orange {
                background: #e6a23c;
            }

            &.dot-gray {
                background: #c0c4cc;
            }
        }

        .tooltip-label {
            flex: 1;
            color: #d0d3d9;
        }

        .tooltip-count {
            font-weight: 600;
            color: #fff;
        }
    }
}
</style>
